import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { FeedbackEvent } from '@bonusly/common-feedback';
import {
	Avatar,
	Box,
	ChatAlt2Outline,
	ChatAltOutline,
	ClappingHandsOutline,
	Divider,
	DotsHorizontalOutline,
	Flex,
	HistoryOutline,
	HStack,
	IconButton,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Portal,
	rem,
	SpotlightOutline,
	TargetOutline,
	Text,
	useDisclosure,
} from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';
import { Permission, ProtectedContent, useCurrentUser } from '@bonusly/who-am-i';

import { CreateReviewModal } from '../../../../components/growth/review-recap/components/create-review-modal';
import { GiveBoxEvent } from '../../../../components/recognition/give-box/events';
import { CreateUserSpotlightModal } from '../../../../components/spotlight';
import { useSpotlightMenuAction } from '../../../../components/spotlight/hooks/use-spotlight-menu-action';
import { useTeamPanel } from '../../../../hooks/use-navbar-panels';
import { useCreateGoalModal } from '../../../../modules/growth/components/goals';
import { generateParticipantKey } from '../../../growth/lib/meeting';
import { ProfileLink } from '../../../profile/components/profile-link';

export interface DirectReportUser {
	id: string;
	username: string;
	displayName: string;
	profilePic: string;
	hasUpcomingOneOnOne?: boolean;
	deletedAt?: string | null;
}

interface DirectReportCardProps {
	user: DirectReportUser;
	subtitle: string;
	onRowClick?: () => void;
	onMenuOpen?: () => void;
	onMenuAction?: (action: string) => void;
}

export const DirectReportCard: FC<DirectReportCardProps> = ({ user, subtitle, onRowClick, onMenuOpen, onMenuAction }) => {
	const { t } = useTranslation(['navigation', 'one_on_ones', 'common']);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const currentUser = useCurrentUser();
	const { close: closeTeamPanel } = useTeamPanel();

	const isActive = user.deletedAt == null;
	const participantKey = generateParticipantKey([currentUser.id, user.id]);

	const spotlight = useSpotlightMenuAction({ userId: user.id, displayName: user.displayName, entryPoint: 'sidebar_team_menu' });

	const createReviewModal = useDisclosure();
	const { onOpen: onOpenGoalModal } = useCreateGoalModal();

	const handleRecognize = () => {
		onMenuAction?.('Give Recognition');
		dispatch(GiveBoxEvent.Open, {
			recipient: { id: user.id, username: user.username },
		});
	};

	const handleGiveFeedback = () => {
		onMenuAction?.('Share Advice');
		dispatch(FeedbackEvent.OpenUnsolicited, {
			defaultUsers: [{ id: user.id, username: user.username }],
		});
	};

	const handleRowClick = () => {
		onRowClick?.();
		closeTeamPanel();
	};

	return (
		<>
			<ProfileLink userId={user.id} onClick={handleRowClick} _hover={{ textDecoration: 'none' }} display="block" data-testid={`nav-link-view-${user.id}`}>
				<Flex
					role="group"
					alignItems="center"
					py={2}
					px={rem(10)}
					mx={rem(-12)}
					h={rem(68)}
					borderRadius="lg"
					_hover={{ bg: 'secondary.bgAlpha' }}
					transition="background 0.15s ease"
					cursor="pointer"
				>
					<Box flexShrink={0}>
						<Avatar name={user.displayName} src={user.profilePic} size="md" />
					</Box>

					<Box w={rem(12)} flexShrink={0} />

					<Box flex={1} minW={0}>
						<Text size="md" variant="medium" color="text.default" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" mb={0}>
							{user.displayName}
						</Text>
						<Text size="sm" color="text.softer" mb={0}>
							{subtitle}
						</Text>
					</Box>

					{isActive && (
						<Box
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
							}}
							flexShrink={0}
							opacity={0}
							_groupHover={{ opacity: 1 }}
							_focusWithin={{ opacity: 1 }}
							transition="opacity 0.15s ease"
						>
							<Menu placement="right-start">
								<MenuButton
									as={IconButton}
									aria-label={t('navigation:teamMenu.menuLabel')}
									variant="ghost"
									size="sm"
									icon={<DotsHorizontalOutline size="sm" />}
									onClick={() => onMenuOpen?.()}
									_hover={{ backgroundColor: 'secondary.bgAlpha' }}
									_active={{ backgroundColor: 'secondary.bgAlpha' }}
									_focusVisible={{ outline: '2px solid', outlineColor: 'border.focus', borderRadius: 'md', outlineOffset: '-2px', opacity: 1 }}
								/>
								<Portal>
									<MenuList
										width="fit-content"
										zIndex={99999}
										px={rem(6)}
										py={rem(8)}
										sx={{
											'& > *:not(:last-child)': { mb: rem(4) },
											'& [role="menuitem"]': {
												minHeight: rem(40),
											},
											'& [role="menuitem"]:focus-visible': {
												outline: '2px solid',
												outlineColor: 'border.focus',
												outlineOffset: '-2px',
												borderRadius: 'sm',
												backgroundColor: 'bg.surface',
											},
										}}
									>
										<ProtectedContent permissions={Permission.CanAccessOneOnOnes}>
											<MenuItem
												onClick={() => {
													onMenuAction?.(user.hasUpcomingOneOnOne ? 'View 1:1' : 'Create 1:1');
													closeTeamPanel();
													navigate(`/meet/with/${participantKey}`);
												}}
											>
												<HStack gap={2}>
													<ChatAlt2Outline />
													<Text>{user.hasUpcomingOneOnOne ? t('navigation:teamMenu.viewOneOnOne') : t('navigation:teamMenu.createOneOnOne')}</Text>
												</HStack>
											</MenuItem>
											<Divider my={1} />
										</ProtectedContent>

										<ProtectedContent permissions={Permission.CanGiveRecognition}>
											<MenuItem onClick={handleRecognize}>
												<HStack gap={2}>
													<ClappingHandsOutline />
													<Text>{t('navigation:teamMenu.giveRecognition')}</Text>
												</HStack>
											</MenuItem>
										</ProtectedContent>

										<ProtectedContent permissions={Permission.CanAccessGoals}>
											<MenuItem
												onClick={() => {
													onMenuAction?.('Create Goal');
													onOpenGoalModal(user.id);
												}}
											>
												<HStack gap={2}>
													<TargetOutline />
													<Text>{t('navigation:teamMenu.createGoal')}</Text>
												</HStack>
											</MenuItem>
										</ProtectedContent>

										<ProtectedContent permissions={Permission.CanAccessAdvice}>
											<MenuItem onClick={handleGiveFeedback}>
												<HStack gap={2}>
													<ChatAltOutline />
													<Text>{t('navigation:teamMenu.shareFeedback')}</Text>
												</HStack>
											</MenuItem>
										</ProtectedContent>

										<ProtectedContent permissions={Permission.CanAccessRecaps}>
											<MenuItem
												onClick={() => {
													onMenuAction?.('Create Custom Recap');
													createReviewModal.onOpen();
												}}
											>
												<HStack gap={2}>
													<HistoryOutline />
													<Text>{t('navigation:teamMenu.createCustomRecap')}</Text>
												</HStack>
											</MenuItem>
										</ProtectedContent>

										{spotlight.isEligible && (
											<MenuItem
												onClick={() => {
													onMenuAction?.('Create Spotlight');
													spotlight.openModal();
												}}
											>
												<HStack gap={2}>
													<SpotlightOutline />
													<Text>{t('navigation:teamMenu.createSpotlight')}</Text>
												</HStack>
											</MenuItem>
										)}
									</MenuList>
								</Portal>
							</Menu>
						</Box>
					)}
				</Flex>
			</ProfileLink>

			<CreateReviewModal userId={user.id} {...createReviewModal} />
			{spotlight.isEligible && (
				<CreateUserSpotlightModal
					isOpen={spotlight.isModalOpen}
					onClose={spotlight.closeModal}
					targetUserId={spotlight.targetUserId}
					targetUserName={spotlight.targetUserName}
					onSuccess={spotlight.closeModal}
					entryPoint={spotlight.entryPoint}
				/>
			)}
		</>
	);
};
